<template>
    <div v-if="regionCheck()" class="bg-white rounded-md shadow-md p-4 relative">
        <div v-if="tooltip" class="info absolute z-10 bg-blue-dark p-4 rounded-xl shadow-xl top-[-70px] w-full">
            <p class="text-white" v-html="$t('subscription_change_to_english', { date: formatTimestamp(abonnement.ending_at) })" />
        </div>
        <div class="absolute cursor-pointer px-1 right-2 top-2">
            <div v-if="abonnement?.userPaymentMethod" class="shadow-md rounded-md border border-blue hover:shadow-xl">
                <img
                    :alt="getNameFromPaymentMethod(abonnement.userPaymentMethod)"
                    class="w-8 h-auto mx-2"
                    :src="$asset('/hkcm/images/payment-methods/' + getImageFromPaymentMethod(abonnement.userPaymentMethod) + '.svg', 'public')"
                    @click="$emit('paymentMethodClicked')"
                />
            </div>
            <div v-else>
                <InfoText icon-text-color="text-red" class="inline-block align-text-bottom ml-2" :text="$t('No_payment_method_attached_yet')" />
            </div>
            <div v-if="region === 'de'" class="relative flex flex-row gap-2 mt-2 mx-auto">
                <i
                    v-if="abonnement.meta.region === 'ch' && abonnement.region !== 'ch'"
                    class="fi fi-rr-info items-center flex absolute right-[80px] top-1"
                    @mouseover="tooltip = true"
                    @mouseleave="tooltip = false"
                />
                <span v-if="enableAboRegionChang" class="shadow-md rounded-md border border-blue hover:shadow-xl flex flex-row px-1 cursor-pointer" @click="modalOpen = true">
                    <span class="right-0 top-0">
                        <div v-if="abonnement.region === 'ch'" class="flex flex-row">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" class="w-6 h-6 mx-auto">
                                <path d="M0,0v30h60V0z" fill="#012169" />
                                <path d="M0,0l60,30m0,-30L0,30" stroke="#fff" stroke-width="6" />
                                <path d="M0,0l60,30m0,-30L0,30" stroke="#C8102E" stroke-width="4" />
                                <path d="M30,0v30M0,15h60" stroke="#fff" stroke-width="10" />
                                <path d="M30,0v30M0,15h60" stroke="#C8102E" stroke-width="6" />
                            </svg>
                            <i class="fi fi-bs-angle-small-right items-center flex"></i>
                        </div>
                        <div v-else class="flex flex-row">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 3" class="w-6 h-6 mx-auto">
                                <rect width="5" height="3" fill="#FFCE00" />
                                <rect width="5" height="2" fill="#DD0000" />
                                <rect width="5" height="1" fill="#000000" />
                            </svg>
                            <i class="fi fi-bs-angle-small-right items-center flex"></i>
                            <svg
                                v-if="abonnement.meta.region === 'ch' && abonnement.region !== 'ch'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 60 30"
                                class="w-6 h-6 mx-auto"
                            >
                                <path d="M0,0v30h60V0z" fill="#012169" />
                                <path d="M0,0l60,30m0,-30L0,30" stroke="#fff" stroke-width="6" />
                                <path d="M0,0l60,30m0,-30L0,30" stroke="#C8102E" stroke-width="4" />
                                <path d="M30,0v30M0,15h60" stroke="#fff" stroke-width="10" />
                                <path d="M30,0v30M0,15h60" stroke="#C8102E" stroke-width="6" />
                            </svg>
                        </div>
                    </span>
                </span>
            </div>
        </div>

        <div class="flex flex-col justify-between h-full">
            <div>
                <div class="flex flex-row gap-4 items-center">
                    <div :class="abonnement.recurring ? '' : 'grayscale'" class="w-[50px] h-[50px] rounded-md flex items-center justify-center text-white">
                        <img :alt="$translate(abonnement.product.name)" class="w-full h-full object-contain" :src="abonnement.product.image.replace('.png', '-gradient.png')" />
                    </div>
                    <div class="w-auto">
                        <strong class="text-sm line-clamp-1 text-ellipsis overflow-hidden relative">
                            {{ $translate(abonnement.product.name) }}
                        </strong>
                        <small>
                            {{ renewalText ? renewalText : getAbonnementPeriod.meta.runtime_in_days === 360 ? $t('12_months') : $translate(getAbonnementPeriod.title) }}
                        </small>
                        <br />
                        <small v-if="abonnement.discountCouponCodeName !== null && abonnement.renewal_discount_code === null">
                            <b
                                >{{ $t('discount_code') }} <span class="font-bold">{{ abonnement.discountCouponCodeName ? abonnement.discountCouponCodeName : '-' }}</span></b
                            ><br />
                            <b
                                >{{ $t('plain_discount') }}:
                                <span class="font-bold">{{
                                    abonnement.discountCouponCodeMeta.discount_percent ? abonnement.discountCouponCodeMeta.discount_percent : '-'
                                }}</span></b
                            >
                        </small>
                        <small v-if="abonnement.renewal_discount_code !== null">
                            <b
                                >{{ $t('discount_code') }} <span class="font-bold">{{ abonnement.couponCodeName ? abonnement.couponCodeName : '-' }}</span></b
                            ><br />
                            <b
                                >{{ $t('plain_discount') }}:
                                <span class="font-bold">{{ abonnement.couponCodeMeta.discount_percent ? abonnement.couponCodeMeta.discount_percent : '-' }}</span></b
                            >
                        </small>
                    </div>
                </div>

                <hr class="border-t border-gray-300 w-full my-4" />

                <div>
                    <table class="w-full text-xs">
                        <tbody>
                            <tr>
                                <td>
                                    {{ $t('booked_at') }}
                                </td>
                                <td class="text-right">
                                    {{ formatDateByRegion($moment(abonnement.starting_at)) }}
                                </td>
                            </tr>
                            <tr v-if="abonnement.recurring">
                                <td>
                                    {{ $t('reccurs_at') }}
                                </td>
                                <td class="text-right">
                                    {{ formatDateByRegion($moment(abonnement.ending_at)) }}
                                </td>
                            </tr>
                            <tr v-else class="text-red">
                                <td>
                                    {{ $t('ending_at') }}
                                </td>
                                <td class="text-right">
                                    {{ formatDateByRegion($moment(abonnement.ending_at)) }}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    {{ $t('initially_purchased_with') }}
                                </td>
                                <td class="text-right">
                                    {{ $t(abonnement.paymentMethod) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <hr class="border-t border-gray-300 w-full my-4" />
            </div>

            <div class="flex flex-col gap-1">
                <div
                    v-if="
                        abonnement.has_bookable_webinar &&
                        abonnement.has_booked_webinar === false &&
                        abonnement.recurring === true &&
                        !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en) &&
                        !global
                    "
                    class="mb-4 flex flex-row gap-2 hover:gap-3 transition-all ease-in-out duration-500 items-center justify-center mt-2 cursor-pointer"
                    @click="$emit('bookWebinar', abonnement)"
                >
                    <span class="text-xs">
                        {{ $t('book_video_analysis') }}
                    </span>
                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="13" class="fill-none stroke-current" />
                </div>
                <div
                    v-if="
                        abonnement.has_bookable_webinar &&
                        abonnement.has_booked_webinar === false &&
                        abonnement.recurring === true &&
                        !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en) &&
                        global
                    "
                    class="border border-gray-300 bg-gray-50 rounded-lg p-4 m-2 max-w-md shadow-sm transition-all"
                >
                    <div class="flex flex-row justify-between items-center text-slate-700 font-semibold mb-3 px-1">
                        <p class="text-sm tracking-wide">Coming Soon</p>
                        <i class="fi fi-tr-video-duration text-2xl text-gray-500"></i>
                    </div>

                    <div
                        class="flex items-center justify-center gap-2 text-slate-700 text-sm bg-white border border-gray-300 hover:border-black hover:bg-gray-100 transition-all duration-300 rounded-lg py-2 px-4 cursor-not-allowed"
                    >
                        <span>{{ $t('book_video_analysis') }}</span>
                        <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="13" class="fill-none stroke-current" />
                    </div>
                </div>

                <div
                    v-if="abonnement.has_booked_webinar === true && abonnement?.booked_webinar?.terminated_at === null"
                    class="mb-4 flex flex-row gap-2 transition-all ease-in-out duration-500 items-center justify-center mt-2"
                >
                    <span class="text-xs">
                        {{ $t('video_analysis_booked') }}
                    </span>
                    <Icon type="Checkmark" viewBox="0 0 121 121" width="13" height="13" class="fill-none stroke-current" />
                </div>

                <div
                    v-if="abonnement?.booked_webinar?.terminated_at && abonnement?.booked_webinar?.ending_at"
                    class="mb-4 flex flex-row gap-2 transition-all ease-in-out duration-500 items-center justify-center mt-2"
                >
                    <span class="text-xs">
                        {{ $t('video_analysis_ending', { ending: formatDateByRegion($moment(abonnement.booked_webinar.ending_at)) }) }}
                    </span>
                </div>

                <div v-if="abonnement.upgradeable && !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en)" class="my-1">
                    <FormButton type="submit" class="w-full" styling="primary xs w-full text-center" :value="$t('change_runtime')" @click="$emit('upgradeAbonnement', abonnement)">
                    </FormButton>
                </div>

                <div v-if="abonnement.upgradeableToParent && !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en)" class="my-1">
                    <FormButton
                        type="submit"
                        class="w-full"
                        styling="primary xs w-full text-center"
                        :value="$t('upgrade_to') + ' ' + abonnement.parentName"
                        @click="$emit('upgradeToParentAbonnement', abonnement)"
                    >
                    </FormButton>
                </div>

                <div
                    v-if="abonnement.resumeable && !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en) && abonnement?.userPaymentMethod"
                    class="my-1"
                >
                    <FormButton type="submit" class="w-full" styling="primary xs w-full text-center" :value="$t('resume_abonnement')" @click="resumeAbonnement(abonnement.id)">
                    </FormButton>
                </div>

                <div v-if="abonnement?.booked_webinar?.terminated_at && abonnement?.booked_webinar?.ending_at && !abonnement.resumeable" class="my-1">
                    <FormButton
                        type="submit"
                        class="w-full"
                        styling="primary xs w-full text-center"
                        :value="$t('subscription_react_video_analysis')"
                        @click="resumeAbonnement(abonnement.booked_webinar.id)"
                    >
                    </FormButton>
                </div>

                <div v-if="hasPaymentMethod && !abonnement?.userPaymentMethod">
                    <FormButton
                        type="submit"
                        class="w-full"
                        styling="bg-red text-white w-full text-center"
                        :value="$t('payment_method_select')"
                        @click="$emit('paymentMethodClicked')"
                    >
                    </FormButton>
                </div>

                <router-link v-if="!hasPaymentMethod" to="/profile/paymentmethods" class="bg-red text-white rounded-lg shadow-xl p-2 text-center cursor-pointer">
                    <p>{{ $t('add_payment_method') }}</p>
                </router-link>

                <div
                    v-if="
                        getAbonnementPeriod.meta.runtime_in_days == 360 &&
                        abonnement.renewal_discount_code == null &&
                        !filter.some((f) => f.de === abonnement.product.name.de && f.en === abonnement.product.name.en) &&
                        abonnement?.userPaymentMethod
                    "
                    class="my-1"
                >
                    <FormButton
                        type="submit"
                        class="w-full"
                        styling="primary xs w-full text-center"
                        :value="$t('subscription_extend')"
                        @click="$emit('extendAbonnement', abonnement)"
                    >
                    </FormButton>
                </div>
            </div>
        </div>
    </div>
    <Popup :overflow-hidden="true" :open="modalOpen" class="p-6" @open="modalOpen = false">
        <div class="p-6 space-y-4">
            <template v-if="!abonnement.meta.region || abonnement.meta.region === 'de'">
                <h2 class="text-xl font-bold text-center">{{ $t('switch_to_english_language') }}</h2>
                <p class="text-center mb-4">{{ $t('do_you_want_to_change_to_english_question', { date: formatTimestamp(abonnement.ending_at) }) }}</p>
                <div v-if="requestSuccess" class="success-checkmark">
                    <div class="check-icon">
                        <span class="icon-line line-tip"></span>
                        <span class="icon-line line-long"></span>
                        <div class="icon-circle"></div>
                        <div class="icon-fix"></div>
                    </div>
                </div>

                <button v-else class="btn primary flex items-center gap-3 w-22 m-auto justify-center" aria-labelledby="button" @click="changeAbonnementRegion">
                    {{ $t('note_the_changeover') }}
                </button>
            </template>
            <template v-else>
                <h2 class="text-xl font-bold text-center">{{ $t('switch_to_english_language') }}</h2>
                <p class="text-center mb-4" v-html="$t('subscription_change_to_english', { date: formatTimestamp(abonnement.ending_at) })" />
                <button class="btn primary flex items-center gap-3 w-20 m-auto justify-center" aria-labelledby="button" @click="cancelChangeAbonnementRegion">
                    {{ $t('Cancel') }}
                </button>
            </template>
        </div>
    </Popup>
</template>

<script>
import i18next from 'i18next';
import { createRouter as $router } from 'vue-router';
import { mapGetters } from 'vuex';
import { formatDateByRegion, formatTimestamp, getImageFromPaymentMethod, getNameFromPaymentMethod } from '../../helpers.js';
import InfoText from '../dashboard/InfoText.vue';
import Popup from '../miscellaneous/Popup.vue';
import Toastification from '../miscellaneous/Toastification.vue';

export default {
    components: { InfoText, Popup },
    props: {
        abonnement: {
            type: Object,
            required: true
        },
        hasPaymentMethod: {
            type: Boolean,
            required: true
        }
    },
    emits: ['reload', 'paymentMethodClicked', 'bookWebinar', 'upgradeAbonnement', 'upgradeToParentAbonnement', 'extendAbonnement'],
    data: () => ({
        enableAboRegionChang: false,
        modalOpen: false,
        loadingRequest: false,
        requestSuccess: false,
        tooltip: false,
        filter: [
            { de: 'Palladium', en: 'Palladium' },
            { de: 'Uran', en: 'Uranium' },
            { de: 'Platin', en: 'Platinum' },
            { de: 'Kupfer', en: 'Copper' },
            { de: 'Industriemetalle', en: 'Industrial metals' }
        ]
    }),
    computed: {
        region() {
            return window.meta.region_configuration.region;
        },
        global() {
            return this.region !== 'de';
        },
        getAbonnementPeriod() {
            return this.abonnement.productVariant.productAttributeValues.data.find((productAttributeValue) => productAttributeValue.meta.type === 'period');
        },
        getAbonnementFrequency() {
            return this.abonnement.productVariant.productAttributeValues.data.find((productAttributeValue) => productAttributeValue.meta.type === 'frequency');
        },
        background() {
            const backgroundColor = this.abonnement.product.productGroups.data[0].productCategories.data[0].background_color;
            const backgroundGradient = this.abonnement.product.productGroups.data[0].productCategories.data[0].background_gradient;

            return 'linear-gradient(to bottom, ' + backgroundColor + ' 0%, ' + backgroundGradient + ' 100%)';
        },
        renewalText() {
            if (this.getAbonnementPeriod.meta.runtime_in_days === 360 && this.abonnement.renewal_discount_code != null) {
                return i18next.t('12_months_renewal_scheduled');
            }

            return '';
        },
        computed: {
            ...mapGetters('payment_methods', {
                getPaymentMethods: 'get'
            })
        }
    },
    methods: {
        $router,
        getNameFromPaymentMethod,
        getImageFromPaymentMethod,
        formatDateByRegion,
        formatTimestamp,
        resumeAbonnement(abonnementVariantId) {
            this.$store
                .dispatch('abonnements/resume', abonnementVariantId)
                .then(() => {
                    this.$toast.success({
                        component: Toastification,
                        props: {
                            message: this.$t('Your Abonnement was succesfully resumed')
                        }
                    });

                    // reload the abonnements in parent component
                    this.$emit('reload');
                })
                .catch(() => {
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: this.$t('There was an Error resuming your Abonnement')
                        }
                    });

                    // reload the abonnements in parent component
                    this.$emit('reload');
                });
        },
        changeAbonnementRegion() {
            this.$store
                .dispatch('abonnements/changeRegion', this.abonnement.id)
                .then(() => {
                    this.requestSuccess = true;
                    this.$toast.success({
                        component: Toastification,
                        props: {
                            message: this.$t('Your Abonnement was succesfully changed')
                        }
                    });

                    this.$emit('reload');
                })
                .catch(() => {
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: this.$t('There was an Error changing your Abonnement')
                        }
                    });
                });
        },
        cancelChangeAbonnementRegion() {
            this.$store
                .dispatch('abonnements/cancelRegionChange', this.abonnement.id)
                .then(() => {
                    this.requestSuccess = true;
                    this.$toast.success({
                        component: Toastification,
                        props: {
                            message: this.$t('Region change cancelled')
                        }
                    });

                    this.$emit('reload');
                })
                .catch(() => {
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: this.$t('There was an Error cancelling your region change')
                        }
                    });
                });
        },
        regionCheck() {
            return this.region === this.abonnement.region;
        }
    }
};
</script>

<style scoped>
.icon {
    background: v-bind(background);
}
.success-checkmark {
    width: 80px;
    height: 80px;
    margin: 0 auto;

    .check-icon {
        width: 80px;
        height: 80px;
        position: relative;
        border-radius: 50%;
        box-sizing: content-box;
        border: 4px solid #4caf50;

        &::before {
            top: 3px;
            left: -2px;
            width: 30px;
            transform-origin: 100% 50%;
            border-radius: 100px 0 0 100px;
        }

        &::after {
            top: 0;
            left: 30px;
            width: 60px;
            transform-origin: 0 50%;
            border-radius: 0 100px 100px 0;
            animation: rotate-circle 4.25s ease-in;
        }

        &::before,
        &::after {
            content: '';
            height: 100px;
            position: absolute;
            background: #ffffff;
            transform: rotate(-45deg);
        }

        .icon-line {
            height: 5px;
            background-color: #4caf50;
            display: block;
            border-radius: 2px;
            position: absolute;
            z-index: 10;

            &.line-tip {
                top: 46px;
                left: 14px;
                width: 25px;
                transform: rotate(45deg);
                animation: icon-line-tip 0.75s;
            }

            &.line-long {
                top: 38px;
                right: 8px;
                width: 47px;
                transform: rotate(-45deg);
                animation: icon-line-long 0.75s;
            }
        }

        .icon-circle {
            top: -4px;
            left: -4px;
            z-index: 10;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            position: absolute;
            box-sizing: content-box;
            border: 4px solid rgba(76, 175, 80, 0.5);
        }

        .icon-fix {
            top: 8px;
            width: 5px;
            left: 26px;
            z-index: 1;
            height: 85px;
            position: absolute;
            transform: rotate(-45deg);
            background-color: #ffffff;
        }
    }
}

@keyframes rotate-circle {
    0% {
        transform: rotate(-45deg);
    }
    5% {
        transform: rotate(-45deg);
    }
    12% {
        transform: rotate(-405deg);
    }
    100% {
        transform: rotate(-405deg);
    }
}

@keyframes icon-line-tip {
    0% {
        width: 0;
        left: 1px;
        top: 19px;
    }
    54% {
        width: 0;
        left: 1px;
        top: 19px;
    }
    70% {
        width: 50px;
        left: -8px;
        top: 37px;
    }
    84% {
        width: 17px;
        left: 21px;
        top: 48px;
    }
    100% {
        width: 25px;
        left: 14px;
        top: 45px;
    }
}

@keyframes icon-line-long {
    0% {
        width: 0;
        right: 46px;
        top: 54px;
    }
    65% {
        width: 0;
        right: 46px;
        top: 54px;
    }
    84% {
        width: 55px;
        right: 0px;
        top: 35px;
    }
    100% {
        width: 47px;
        right: 8px;
        top: 38px;
    }
}
</style>
